/* Orbit Ring - turntable product carousel, scroll-scrub edition.
   Scroll drives the ring; after the last product the section releases.
   Autoplay drift spins the ring while the visitor is idle. */
if (!customElements.get('orbit-ring')) {
  customElements.define(
    'orbit-ring',
    class OrbitRing extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized) return;
        this.dataset.initialized = 'true';
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const dataEl = this.querySelector('[data-orbit-data]');
        try { this.products = JSON.parse(dataEl.textContent); } catch (e) { this.products = []; }
        if (!this.products.length) return;

        this.opts = {
          autoplay: this.dataset.autoplay === 'true',
          speed: (parseFloat(this.dataset.speed) || 3) * 0.1,
          dupFill: this.dataset.dupFill === 'true',
          newTab: this.dataset.newTab === 'true',
          quickAdd: this.dataset.quickAdd === 'true',
          imageStyle: this.dataset.imageStyle || 'circle',
        };

        this.ringEl = this.querySelector('[data-orbit-ring]');
        this.featEl = this.querySelector('[data-orbit-feat]');
        this.capEl = this.querySelector('[data-orbit-cap]');
        this.codeEl = this.querySelector('[data-orbit-code]');
        this.titleEl = this.querySelector('[data-orbit-title]');
        this.priceEl = this.querySelector('[data-orbit-price]');
        this.dotsEl = this.querySelector('[data-orbit-dots]');
        this.counterEl = this.querySelector('[data-orbit-counter]');
        this.addBtn = this.querySelector('[data-orbit-add]');
        this.collEl = this.querySelector('[data-orbit-coll]');
        this.sheet = this.querySelector('[data-orbit-sheet]');
        this.sheetBg = this.querySelector('[data-orbit-sheet-bg]');
        this.srEl = this.querySelector('[data-orbit-sr]');
        this.stage = this.querySelector('[data-orbit-stage]');

        this.slots = Array.from(this.ringEl.querySelectorAll('[data-orbit-slot]'));
        const MIN_RING = 8;
        if (this.opts.dupFill && this.slots.length > 1) {
          let src = 0;
          while (this.slots.length < MIN_RING) {
            const clone = this.slots[src % this.products.length].cloneNode(true);
            clone.dataset.dup = 'true';
            this.ringEl.appendChild(clone);
            this.slots.push(clone);
            src++;
          }
        }
        this.slotProd = this.slots.map((el) => parseInt(el.dataset.index, 10));

        this.N = this.slots.length;
        this.STEP = (2 * Math.PI) / this.N;
        this.PER = 110;
        this.angle = 0; this.drift = 0; this.lastTarget = null;
        this.lastInteract = performance.now() - 2000;
        this.focused = -1; this.axis = null; this.hDrag = false; this.swiped = false;
        this.imgIdx = this.products.map(() => 0);
        this.featImgEl = null; this.raf = 0;

        /* small photo arrows flanking the hero */
        this.prevBtn = this.mkArrow('M15 18l-6-6 6-6', 'Previous photo', -1);
        this.nextBtn = this.mkArrow('M9 6l6 6-6 6', 'Next photo', 1);

        this.classList.add('orbit-ready');
        this.measure();
        this.onResize = () => { this.measure(); this.render(true); };
        window.addEventListener('resize', this.onResize);

        this.slots.forEach((el, i) => {
          el.addEventListener('click', (ev) => {
            ev.preventDefault();
            if (this.swiped) { this.swiped = false; return; }
            const d = this.shortest(i * this.STEP - this.angle);
            if (Math.abs(d) < this.STEP * 0.4) {
              if (this.opts.newTab) window.open(el.href, '_blank');
              else window.location.href = el.href;
            } else this.spinTo(i);
          });
          this.classify(this.slotProd[i], el.querySelector('img'));
        });

        this.bindGestures();
        this.bindSheet();

        this.render(true);
        this.last = performance.now();
        const loop = (now) => { this.tickFrame(now); this.raf = requestAnimationFrame(loop); };
        this.raf = requestAnimationFrame(loop);
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.raf);
        window.removeEventListener('resize', this.onResize);
        delete this.dataset.initialized;
      }

      mkArrow(path, label, dir) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'orbit-arrow ' + (dir < 0 ? 'orbit-arrow--l' : 'orbit-arrow--r');
        b.setAttribute('aria-label', label);
        b.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="' + path + '"></path></svg>';
        b.addEventListener('click', () => this.cycleImg(dir));
        this.stage.appendChild(b);
        return b;
      }

      measure() {
        /* pin below the site's sticky header so nothing is hidden behind it */
        const hdr = document.querySelector('sticky-header');
        this.topOff = hdr ? Math.round(hdr.getBoundingClientRect().height) : 0;
        this.stage.style.top = this.topOff + 'px';
        this.stage.style.height = '';
        const nat = this.stage.offsetHeight || Math.round(window.innerHeight * 0.85);
        const h = Math.max(420, Math.min(nat, window.innerHeight - this.topOff));
        this.stage.style.height = h + 'px';
        this.travel = (this.N - 1) * this.PER;
        this.style.height = (h + this.travel) + 'px';
        const r = this.stage.getBoundingClientRect();
        this.W = r.width; this.H = r.height;
        this.CX = this.W / 2; this.CY = this.H * 0.40;
        /* desktop scales the whole instrument up so the ring fills the width */
        const desk = this.W >= 990;
        this.RX = Math.min(this.W * 0.44, desk ? 550 : 300);
        this.RY = Math.min(this.H * 0.26, this.RX);
        this.THUMB = desk ? Math.min(110, this.W * 0.11) : Math.max(46, Math.min(76, this.W * 0.17));
        this.featEl.style.top = this.CY + 'px';
        if (this.dotsEl) this.dotsEl.style.top = (this.CY + this.featEl.offsetHeight / 2 + 12) + 'px';
        if (this.prevBtn) {
          /* hug the actual photo circle (mirrors the CSS sizes per breakpoint) */
          const vph = window.innerHeight;
          const circ = desk ? Math.min(this.W * 0.40, vph * 0.30, 320) : Math.min(this.W * 0.48, vph * 0.27, 270);
          const half = circ / 2 + 26;
          this.prevBtn.style.top = this.CY + 'px';
          this.nextBtn.style.top = this.CY + 'px';
          this.prevBtn.style.left = (this.CX - half) + 'px';
          this.nextBtn.style.left = (this.CX + half) + 'px';
        }
        this.capEl.style.top = (this.CY + this.RY + this.THUMB * 0.65 + 14) + 'px';
      }
      mod(a, n) { return ((a % n) + n) % n; }
      shortest(d) { const T = 2 * Math.PI; return this.mod(d + Math.PI, T) - Math.PI; }
      slotOf(a) { return this.mod(Math.round(a / this.STEP), this.N); }
      userPing() { this.lastInteract = performance.now(); this.drift = this.shortest(this.drift); }

      classify(pi, imgEl) {
        const p = this.products[pi];
        if (p.style) { this.applyStyle(p, imgEl); return; }
        const im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = () => {
          try {
            const S = 48, c = document.createElement('canvas');
            c.width = c.height = S;
            const x = c.getContext('2d', { willReadFrequently: true });
            x.drawImage(im, 0, 0, S, S);
            const d = x.getImageData(0, 0, S, S).data;
            let trans = 0;
            for (let i = 3; i < d.length; i += 4) if (d[i] < 40) trans++;
            if (trans > S * S * 0.05) { p.style = 'cut'; }
            else {
              const pts = [[1, 1], [S - 2, 1], [1, S - 2], [S - 2, S - 2]];
              let white = 0;
              for (const [qx, qy] of pts) {
                const i = (qy * S + qx) * 4;
                if (d[i] > 224 && d[i + 1] > 224 && d[i + 2] > 224) white++;
              }
              p.style = white >= 4 ? 'key' : 'tile';
            }
          } catch (e) { p.style = 'tile'; }
          if (p.style === 'key') {
            this.whiteKey(p.thumb, (u) => { p.thumb = u; this.refreshProduct(pi); });
            this.whiteKey(p.imgs[0], (u) => { p.imgs[0] = u; this.refreshProduct(pi); });
            p.style = 'cut';
          }
          this.refreshProduct(pi);
        };
        im.onerror = () => { p.style = 'tile'; this.refreshProduct(pi); };
        im.src = p.thumb;
      }
      whiteKey(src, cb) {
        const im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = () => {
          try {
            const w = im.naturalWidth, h = im.naturalHeight;
            const c = document.createElement('canvas'); c.width = w; c.height = h;
            const x = c.getContext('2d'); x.drawImage(im, 0, 0);
            const id = x.getImageData(0, 0, w, h), d = id.data, T = 224;
            const vis = new Uint8Array(w * h), q = [];
            const push = (px, py) => {
              if (px < 0 || py < 0 || px >= w || py >= h) return;
              const k = py * w + px;
              if (vis[k]) return;
              const i = k * 4;
              if (d[i] > T && d[i + 1] > T && d[i + 2] > T) { vis[k] = 1; q.push(k); }
            };
            for (let i = 0; i < w; i++) { push(i, 0); push(i, h - 1); }
            for (let j = 0; j < h; j++) { push(0, j); push(w - 1, j); }
            while (q.length) {
              const k = q.pop(), py = (k / w) | 0, px = k % w;
              push(px + 1, py); push(px - 1, py); push(px, py + 1); push(px, py - 1);
            }
            for (let k = 0; k < w * h; k++) if (vis[k]) d[k * 4 + 3] = 0;
            x.putImageData(id, 0, 0);
            cb(c.toDataURL('image/png'));
          } catch (e) { /* leave as-is */ }
        };
        im.src = src;
      }
      applyStyle(p, imgEl) { imgEl.classList.toggle('orbit-tile', p.style === 'tile'); }
      refreshProduct(pi) {
        const p = this.products[pi];
        this.slots.forEach((el, i) => {
          if (this.slotProd[i] === pi) {
            const im = el.querySelector('img');
            im.src = p.thumb;
            this.applyStyle(p, im);
          }
        });
        if (this.focused >= 0 && this.slotProd[this.focused] === pi) this.setFocused(this.focused, true);
      }

      render(force) {
        for (let i = 0; i < this.N; i++) {
          const th = this.shortest(i * this.STEP - this.angle);
          const x = this.CX + this.RX * Math.sin(th);
          const y = this.CY - this.RY * Math.cos(th);
          const depth = (1 - Math.cos(th)) / 2;
          const sc = 0.72 + 0.5 * depth;
          const ghost = Math.min(1, Math.abs(th) / (this.STEP * 0.62));
          const el = this.slots[i], sz = this.THUMB;
          const op = 0.14 + 0.86 * ghost;
          el.style.width = el.style.height = sz + 'px';
          el.style.transform = 'translate(' + (x - sz / 2) + 'px,' + (y - sz / 2) + 'px) scale(' + sc + ')';
          el.style.opacity = op.toFixed(3);
          el.style.zIndex = 10 + Math.round(depth * 20);
          el.style.pointerEvents = op < 0.25 ? 'none' : 'auto';
        }
        const f = this.slotOf(this.angle);
        if (f !== this.focused || force) this.setFocused(f, force);
      }

      setFocused(f, instant) {
        this.focused = f;
        const pi = this.slotProd[f], p = this.products[pi];
        this.haptic(!instant);
        if (this.counterEl) this.scramble(this.counterEl, String(pi + 1).padStart(2, '0') + ' / ' + this.products.length, instant);
        this.scramble(this.codeEl, p.code, instant);
        this.scramble(this.titleEl, p.title, instant);
        this.scramble(this.priceEl, p.price, instant);
        this.drawDots(p, this.imgIdx[pi]);
        if (this.srEl) this.srEl.textContent = p.title + ', ' + p.price;
        this.swapFeatured(p.imgs[this.imgIdx[pi]], this.isPhoto(p, this.imgIdx[pi]), instant ? null : 'top');
        this.featEl.setAttribute('href', p.url);
        this.featEl.setAttribute('aria-label', p.title);
        if (this.collEl) {
          if (p.curl) {
            this.collEl.style.display = '';
            this.collEl.setAttribute('href', p.curl);
          } else {
            this.collEl.style.display = 'none';
          }
        }
      }
      isPhoto(p, ii) { return ii > 0 || p.style === 'tile'; }

      swapFeatured(src, isPhoto, from) {
        const old = this.featImgEl;
        this.featEl.querySelectorAll('.orbit-fimg').forEach((el) => { if (el !== old) el.remove(); });
        const im = document.createElement('img');
        im.className = 'orbit-fimg' + (isPhoto ? (this.opts.imageStyle === 'card' ? ' orbit-photo-card' : ' orbit-photo') : '');
        im.src = src; im.alt = ''; im.draggable = false;
        this.featEl.appendChild(im);
        this.featImgEl = im;
        if (this.reduced || !from) { if (old) old.remove(); return; }
        const topY = -this.RY;
        const fromT = from === 'top' ? 'translateY(' + (topY * 0.9) + 'px) scale(.22)' :
          from === 'left' ? 'translateX(-46px) scale(.94)' : 'translateX(46px) scale(.94)';
        im.animate([{ transform: fromT, opacity: 0.25 }, { transform: 'none', opacity: 1 }],
          { duration: from === 'top' ? 400 : 280, easing: 'cubic-bezier(.22,1.08,.34,1)' });
        if (old) {
          const toT = from === 'top' ? 'translateY(' + (topY * 0.6) + 'px) scale(.4)' :
            from === 'left' ? 'translateX(46px) scale(.94)' : 'translateX(-46px) scale(.94)';
          old.animate([{ opacity: 1 }, { transform: toT, opacity: 0 }],
            { duration: from === 'top' ? 300 : 240, easing: 'ease-out' }).onfinish = () => old.remove();
          setTimeout(() => old.remove(), 450);
        }
      }
      drawDots(p, ii) {
        const many = p.imgs.length > 1;
        if (this.prevBtn) {
          this.prevBtn.style.display = many ? '' : 'none';
          this.nextBtn.style.display = many ? '' : 'none';
        }
        if (!this.dotsEl) return;
        this.dotsEl.innerHTML = '';
        if (!many) return;
        p.imgs.forEach((_, k) => {
          const d = document.createElement('i');
          if (k === ii) d.className = 'on';
          this.dotsEl.appendChild(d);
        });
      }
      cycleImg(dir) {
        this.userPing();
        const pi = this.slotProd[this.focused], p = this.products[pi];
        if (p.imgs.length < 2) return;
        const ii = this.mod(this.imgIdx[pi] + dir, p.imgs.length);
        this.imgIdx[pi] = ii;
        this.swapFeatured(p.imgs[ii], this.isPhoto(p, ii), dir > 0 ? 'right' : 'left');
        this.drawDots(p, ii);
        this.haptic(true);
      }
      haptic(fx) { if (fx && navigator.vibrate) { try { navigator.vibrate(4); } catch (e) { } } }

      scramble(el, txt, instant) {
        if (!el) return;
        if (el._t) cancelAnimationFrame(el._t);
        if (instant || this.reduced) { el.textContent = txt; return; }
        const CHARS = '#*01ABCDEFGHKMNPRSTUVXYZ/';
        let fr = 0; const total = 13;
        const run = () => {
          fr++;
          const lock = Math.floor(txt.length * fr / total);
          let s = '';
          for (let i = 0; i < txt.length; i++)
            s += i < lock || txt[i] === ' ' ? txt[i] : CHARS[(Math.random() * CHARS.length) | 0];
          el.textContent = s;
          if (fr < total) el._t = requestAnimationFrame(run); else el._t = null;
        };
        run();
      }

      spinTo(i) {
        this.userPing();
        if (this.N < 2) return;
        const rect = this.getBoundingClientRect();
        const top = window.scrollY + rect.top - this.topOff + (i / (this.N - 1)) * this.travel;
        window.scrollTo({ top: top, behavior: this.reduced ? 'auto' : 'smooth' });
      }

      tickFrame(now) {
        const dt = Math.min(0.05, (now - this.last) / 1000); this.last = now;
        const rect = this.getBoundingClientRect();
        const travel = Math.max(1, this.travel);
        const prog = Math.min(1, Math.max(0, (this.topOff - rect.top) / travel));
        const target = prog * (this.N - 1) * this.STEP;
        if (this.lastTarget !== null && Math.abs(target - this.lastTarget) > 0.001) {
          this.userPing();
        }
        this.lastTarget = target;
        if (this.opts.autoplay && !this.reduced && now - this.lastInteract > 2500 && this.inView()) {
          this.drift += this.opts.speed * dt;
        } else if (this.drift !== 0) {
          this.drift = Math.abs(this.drift) < 0.002 ? 0 : this.drift * Math.pow(0.02, dt);
        }
        const goal = target + this.drift;
        const d = goal - this.angle;
        if (Math.abs(d) > 0.0004) {
          this.angle += Math.abs(d) < 0.002 ? d : d * Math.min(1, dt * 14);
          this.render();
        }
      }
      inView() {
        const r = this.getBoundingClientRect();
        return r.bottom > 0 && r.top < window.innerHeight;
      }

      bindGestures() {
        const st = this.stage;
        /* desktop: stop native link/image drag from hijacking the pointer */
        st.addEventListener('dragstart', (e) => { e.preventDefault(); });
        st.addEventListener('pointerdown', (e) => {
          if (e.target.closest('[data-orbit-sheet],[data-orbit-add],[data-orbit-coll],button')) return;
          this.userPing();
          this.hDrag = true; this.axis = null; this.swiped = false;
          this.px = e.clientX; this.py = e.clientY; this.dragDX = 0;
          this.pid = e.pointerId;
        });
        st.addEventListener('pointermove', (e) => {
          if (!this.hDrag) return;
          const dx = e.clientX - this.px, dy = e.clientY - this.py;
          if (!this.axis) {
            if (Math.hypot(dx, dy) < 9) return;
            this.axis = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
            if (this.axis === 'v') { this.hDrag = false; return; }
            /* horizontal drag confirmed: capture so mouse-release outside still lands */
            try { st.setPointerCapture(this.pid); } catch (err) { }
          }
          this.dragDX = dx;
          if (this.featImgEl && !this.reduced) this.featImgEl.style.transform = 'translateX(' + (dx * 0.3) + 'px)';
        });
        st.addEventListener('pointerup', () => {
          if (!this.hDrag) return;
          this.hDrag = false;
          if (this.axis === 'h') {
            this.swiped = true;
            if (this.featImgEl) this.featImgEl.style.transform = '';
            if (Math.abs(this.dragDX) > 42) this.cycleImg(this.dragDX < 0 ? 1 : -1);
          }
          this.axis = null;
        });
        st.addEventListener('pointercancel', () => {
          this.hDrag = false; this.axis = null; this.swiped = false;
          if (this.featImgEl) this.featImgEl.style.transform = '';
        });
        /* a drag that ends on the featured link must not navigate */
        this.featEl.addEventListener('click', (ev) => {
          if (this.swiped) { ev.preventDefault(); ev.stopPropagation(); this.swiped = false; }
        });

        this.stage.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            this.spinTo(Math.max(0, Math.min(this.N - 1, this.slotOf(this.angle) + (e.key === 'ArrowDown' ? 1 : -1))));
          }
          if (e.key === 'ArrowRight') { e.preventDefault(); this.cycleImg(1); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.cycleImg(-1); }
        });
      }

      bindSheet() {
        if (!this.addBtn || !this.sheet) return;
        this.addBtn.addEventListener('click', () => {
          const p = this.products[this.slotProd[this.focused]];
          if (p.vx === undefined) p.vx = this.parseVx(p);
          if (p.vx) this.openSheet(p);
          else this.addToCart(p.vars[0][0], p);
        });
        const close = () => this.toggleSheet(false);
        if (this.sheetBg) this.sheetBg.addEventListener('click', close);
        this.sheet.querySelector('[data-orbit-sheet-close]').addEventListener('click', close);
        this.sheet.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
        this.vAddBtn = this.sheet.querySelector('[data-orbit-vadd]');
        this.vAddBtn.addEventListener('click', () => {
          const v = this.variantFor(this.vSel);
          if (!v || !v[2]) return;
          this.toggleSheet(false);
          this.addToCart(v[0], this.vProd);
        });
      }
      parseVx(p) {
        const vs = p.vars || [];
        if (!vs.length || (vs.length === 1 && vs[0][1] === 'Default Title')) return null;
        const parts = vs.map((v) => v[1].split(' / '));
        const nAx = Math.max.apply(null, parts.map((a) => a.length));
        const axes = [];
        for (let k = 0; k < nAx; k++) {
          const seen = [];
          parts.forEach((a) => { const t = a[k] || ''; if (t && !seen.includes(t)) seen.push(t); });
          axes.push(seen);
        }
        return { axes, parts, vs };
      }
      pillLabel(val) {
        const SIZE_SHORT = [['xx-large', '2XL'], ['x-large', 'XL'], ['small', 'S'], ['medium', 'M'], ['large', 'L']];
        let base = val, sub = null;
        const op = val.indexOf('(');
        if (op > -1) {
          base = val.slice(0, op).trim();
          const cl = val.indexOf(')', op);
          sub = cl > -1 ? val.slice(op + 1, cl) : null;
        }
        const low = base.toLowerCase();
        for (const pair of SIZE_SHORT) if (low.indexOf(pair[0]) === 0) return { main: pair[1], sub: sub };
        return { main: base.toUpperCase(), sub: sub };
      }
      variantFor(sel) {
        const vx = this.vProd.vx;
        for (let i = 0; i < vx.parts.length; i++)
          if (sel.every((v, k) => (vx.parts[i][k] || '') === v)) return vx.vs[i];
        return null;
      }
      availWith(axis, val) {
        const s2 = this.vSel.slice(); s2[axis] = val;
        const vx = this.vProd.vx;
        for (let i = 0; i < vx.parts.length; i++)
          if (s2.every((v, k) => (vx.parts[i][k] || '') === v) && vx.vs[i][2]) return true;
        return false;
      }
      renderAxes() {
        const SWATCH = { black: '#1D1D1F', white: '#FFFFFF', grey: '#9AA0A6', gray: '#9AA0A6', cream: '#EFE9DC', beige: '#D9CDB8', green: '#3E6B4F', blue: '#3B5B8C', red: '#B3452E', pink: '#D98BA6', navy: '#22304A' };
        const vx = this.vProd.vx, host = this.sheet.querySelector('[data-orbit-axes]');
        host.innerHTML = '';
        vx.axes.forEach((vals, k) => {
          const isColor = vals.every((v) => SWATCH[v.toLowerCase().trim()]);
          const ax = document.createElement('div'); ax.className = 'orbit-vaxis';
          const row = document.createElement('div'); row.className = 'orbit-vrow';
          vals.forEach((val) => {
            const ok = this.availWith(k, val);
            let b = document.createElement('button');
            b.type = 'button';
            if (isColor) {
              b.className = 'orbit-vsw' + (this.vSel[k] === val ? ' on' : '');
              b.style.background = SWATCH[val.toLowerCase().trim()];
              b.setAttribute('aria-label', val);
            } else {
              b.className = 'orbit-vp' + (this.vSel[k] === val ? ' on' : '');
              const L = this.pillLabel(val);
              b.innerHTML = L.main + (L.sub ? '<small>' + L.sub + '</small>' : '');
            }
            b.disabled = !ok;
            b.addEventListener('click', () => { this.vSel[k] = val; this.renderAxes(); });
            row.appendChild(b);
          });
          ax.appendChild(row); host.appendChild(ax);
        });
        const v = this.variantFor(this.vSel);
        this.vAddBtn.disabled = !v || !v[2];
        this.vAddBtn.textContent = v && v[2] ? (this.dataset.addToCartLabel || 'Add to cart') + ' - ' + v[3] : (this.dataset.soldOutLabel || 'Sold out');
      }
      openSheet(p) {
        this.vProd = p;
        const vx = p.vx;
        this.vSel = vx.axes.map((a) => a[0]);
        for (let i = 0; i < vx.parts.length; i++) if (vx.vs[i][2]) { this.vSel = vx.axes.map((a, k) => vx.parts[i][k] || a[0]); break; }
        this.sheet.querySelector('[data-orbit-sheet-title]').textContent = p.title;
        this.renderAxes();
        this.toggleSheet(true);
      }
      toggleSheet(on) {
        this.sheet.classList.toggle('on', on);
        if (this.sheetBg) this.sheetBg.classList.toggle('on', on);
        if (on) this.vAddBtn.focus();
      }

      addToCart(variantId, p) {
        if (!this.opts.quickAdd) { window.location.href = p.url; return; }
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = new FormData();
        body.append('id', variantId);
        body.append('quantity', '1');
        if (cart && cart.getSectionsToRender) {
          body.append('sections', cart.getSectionsToRender().map((s) => s.id));
          body.append('sections_url', window.location.pathname);
          if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
        }
        if (this.addBtn) this.addBtn.classList.add('loading');
        fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
          body: body,
        })
          .then((r) => r.json())
          .then((response) => {
            if (response.status) { window.location.href = p.url; return; }
            if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'orbit-ring', productVariantId: variantId, cartData: response });
            }
            if (cart && cart.renderContents) {
              if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
              cart.renderContents(response);
            }
          })
          .catch(() => { window.location.href = p.url; })
          .finally(() => { if (this.addBtn) this.addBtn.classList.remove('loading'); });
      }
    }
  );
}